import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"

export interface PaymentReceipt {
  id: string
  charge_id: string
  amount_cents: number
  method: string
  provider?: string
  transaction_id?: string
  receipt_url?: string
  paid_at: string
  recorded_by?: string
  created_at: string
  updated_at: string
}

export const usePaymentReceipts = (chargeId: string) => {
  return useQuery({
    queryKey: ["payment-receipts", chargeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_receipts")
        .select("*")
        .eq("charge_id", chargeId)
        .order("paid_at", { ascending: false })

      if (error) throw error
      return data as PaymentReceipt[]
    },
    enabled: !!chargeId
  })
}

export const useCreatePaymentReceipt = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (receiptData: Omit<PaymentReceipt, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from("payment_receipts")
        .insert(receiptData)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["payment-receipts", data.charge_id] })
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Recibo de pagamento registrado com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao criar recibo:", error)
      toast.error("Erro ao registrar recibo de pagamento")
    }
  })
}

export const useDeletePaymentReceipt = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, chargeId }: { id: string; chargeId: string }) => {
      const { error } = await supabase
        .from("payment_receipts")
        .delete()
        .eq("id", id)

      if (error) throw error
      return { chargeId }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["payment-receipts", data.chargeId] })
      queryClient.invalidateQueries({ queryKey: ["financial-charges"] })
      toast.success("Recibo removido com sucesso!")
    },
    onError: (error) => {
      console.error("Erro ao remover recibo:", error)
      toast.error("Erro ao remover recibo")
    }
  })
}
